import { Marked } from 'marked'

// Liquid ({{ output }} / {% tags %}) is not markdown, but it contains
// markdown-significant characters (_, *, URLs, >). Round-tripping it through
// marked (load) and turndown (save) mangles it — e.g. `{{ profit_loss }}`
// becomes `{{ profit\_loss }}`, breaking the variable. To avoid that we treat
// each Liquid span as opaque, like a code span: mask it out before marked runs
// and restore it as plain text (highlighted in the editor by the
// liquid-decoration extension), then skip markdown escaping over it on save
// (see liquidAwareEscape).

export interface LiquidSpan {
  start: number
  end: number
  raw: string
}

// Private-use sentinels: marked leaves them as plain text (not markdown-
// significant, not URL-autolinked below), so they survive the parse untouched
// and are trivial to find again in the resulting HTML.
const MASK_OPEN = '\uE000'
const MASK_CLOSE = '\uE001'

// A sentinel inside a Markdown link URL \u2014 [text]({{ id }}) \u2014 is run through
// marked's href cleaner (encodeURI), which percent-encodes the multi-byte
// private-use characters (\uE000 -> %EE%80%80). The digit index in between is
// URI-safe and survives. Match either the raw sentinels or their encodeURI form
// so restoration works in link hrefs too (the enclosed index is untouched).
const OPEN_ALT = `${MASK_OPEN}|${encodeURI(MASK_OPEN)}`
const CLOSE_ALT = `${MASK_CLOSE}|${encodeURI(MASK_CLOSE)}`
const MASK_REGEX = new RegExp(`(?:${OPEN_ALT})(\\d+)(?:${CLOSE_ALT})`, 'g')

// Scans left-to-right for {{ ... }} and {% ... %} spans, staying quote-aware so
// a }} or %} inside a string literal (e.g. `{{ x | default: "}}" }}`) does not
// close the span early. Whitespace-control variants ({{- -}} / {%- -%}) fall
// out for free since they still end in }} / %}.
export const findLiquidSpans = (text: string): LiquidSpan[] => {
  const spans: LiquidSpan[] = []
  let i = 0

  while (i < text.length) {
    const isOutput = text[i] === '{' && text[i + 1] === '{'
    const isTag = text[i] === '{' && text[i + 1] === '%'

    if (isOutput || isTag) {
      const closing = isOutput ? '}}' : '%}'
      const end = findClosing(text, i + 2, closing)
      if (end === -1) break // unterminated — leave the remainder as plain text
      spans.push({ start: i, end, raw: text.slice(i, end) })
      i = end
    } else {
      i += 1
    }
  }

  return spans
}

const findClosing = (text: string, from: number, closing: string): number => {
  let quote: string | null = null

  for (let i = from; i < text.length; i += 1) {
    const ch = text[i]
    if (quote) {
      if (ch === quote) quote = null
    } else if (ch === '"' || ch === "'") {
      quote = ch
    } else if (ch === closing[0] && text[i + 1] === closing[1]) {
      return i + 2
    }
  }

  return -1
}

export const maskLiquid = (
  text: string
): { masked: string; tokens: string[] } => {
  const spans = findLiquidSpans(text)
  if (spans.length === 0) return { masked: text, tokens: [] }

  const tokens: string[] = []
  let masked = ''
  let last = 0

  spans.forEach((span) => {
    masked += text.slice(last, span.start)
    masked += `${MASK_OPEN}${tokens.length}${MASK_CLOSE}`
    tokens.push(span.raw)
    last = span.end
  })
  masked += text.slice(last)

  return { masked, tokens }
}

// Escapes the five characters that are significant in HTML text or attribute
// context. Quotes matter because a restored span can land inside a double-quoted
// href — [Report](https://app/{{ x | default: "y" }}) — where an unescaped `"`
// would close the attribute early and truncate the URL.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// Restore masked spans as plain (html-escaped) text: the Liquid re-enters the
// document as ordinary editable text, and the liquid-decoration extension
// highlights it. No wrapping node — that is the point of the non-atomic design.
export const restoreLiquidHtml = (html: string, tokens: string[]): string =>
  html.replace(MASK_REGEX, (_match, index: string) =>
    escapeHtml(tokens[Number(index)] ?? '')
  )

// marked instance with URL autolinking disabled: otherwise a masked span inside
// a URL (https://.../{{ id }}/...) gets pulled into the autolink and the
// sentinel is percent-encoded into the href, corrupting both the URL and the
// Liquid. Disabling it also keeps bare URLs literal, which is what a Liquid
// template field wants anyway.
const liquidMarked = new Marked({ gfm: true })
liquidMarked.use({ tokenizer: { url: () => undefined } })

export const parseLiquidToHtml = async (markdown: string): Promise<string> => {
  const { masked, tokens } = maskLiquid(markdown)
  const html = await liquidMarked.parse(masked)
  return restoreLiquidHtml(html, tokens)
}

// Liquid-aware replacement for turndown's text escaper. turndown escapes
// markdown-significant characters in every text node (`_` -> `\_`, etc.), which
// would corrupt Liquid source. Since the source is now plain text (no node to
// match with a rule), we instead escape only the non-Liquid segments and copy
// each Liquid span through verbatim — the same idea as turndown leaving inline
// code untouched. `escape` is turndown's original escaper, passed in so the
// prose around the Liquid keeps its usual escaping.
export const liquidAwareEscape = (
  text: string,
  escape: (value: string) => string
): string => {
  const spans = findLiquidSpans(text)
  if (spans.length === 0) return escape(text)

  let result = ''
  let last = 0

  spans.forEach((span) => {
    result += escape(text.slice(last, span.start))
    result += span.raw
    last = span.end
  })
  result += escape(text.slice(last))

  return result
}
